import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';

import { useCapabilities } from 'hooks/useCapabilities/useCapabilities';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import {
  setDataStores,
  setDestinationProcess,
  setMessageState,
  setProcessSpecificationFields,
  setSourceProcess,
} from 'stores/webedi/webedi';
import { DEFAULT_REFETCH_INTERVAL, EMPTY_FIELD } from 'support/helpers/const/const';
import { isMessageBEValidationComplete, isReceiverDeliveryInFlight } from 'support/helpers/messages/messages';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { usePrefetchOCRData } from 'support/helpers/ocr/ocr';
import { RouteParams } from 'support/helpers/types/types';
import { TransactionsRoutes, TransactionState } from 'support/types';
import { AIBadge } from 'components/Display/AIBadge/AIBadge';
import { Page } from 'components/Page/Page';

import { useAcknowledgement } from './hooks/useAcknowledgement';
import { useIsAdminOCRTest } from './hooks/useIsAdminOCRTest';
import { revealPartyFields } from '../../helpers';
import { ExistingMessageUI } from './ExistingMessageUI/ExistingMessageUI';
import { MissingMessageUI } from './MissingMessageUI/MissingMessageUI';
import { UnknownTypePlaceholder } from './UnknownTypePlaceholder';

export function MessageViewPage() {
  useInitWebEDIStore();
  const { id: messageId } = useParams<{ id: string }>();
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const [shouldPollMessage, setShouldPollMessage] = useState(false);
  const {
    data: message,
    isError,
    isLoading: isLoadingMessage,
  } = useMessageWithProcessSpecification({
    variables: {
      messageId,
    },
    refetchInterval: shouldPollMessage ? DEFAULT_REFETCH_INTERVAL : false,
  });
  const messageData = message?.data;
  const messageFields = message?.fields;
  const isCurrentUserAdmin = currentUser?.isAdmin;

  /**
   * A receiver-side resend replays the receiver envelope without re-running canonicalization, so
   * `internal_state` stays terminal while the redelivery is in flight. Watching the envelope too
   * is what lets the page see the resend land instead of showing pending until a reload.
   */
  useEffect(() => {
    const internalState = messageData?.internal_state;
    const receiverEnvelopeState = messageData?.receiver_envelope?.state;
    setShouldPollMessage(
      !isMessageBEValidationComplete(internalState) || isReceiverDeliveryInFlight(receiverEnvelopeState),
    );
  }, [messageData?.internal_state, messageData?.receiver_envelope?.state]);

  const fields = useMemo(() => {
    if (!messageFields || !isCurrentUserAdmin) {
      return messageFields;
    }
    return revealPartyFields(messageFields);
  }, [messageFields, isCurrentUserAdmin]);

  useCapabilities({ messageId });
  useIsAdminOCRTest(messageId);
  useAcknowledgement(messageData, currentPartner, currentUser, messageData?.type);

  const { isLoading: isPrefetchingOCRRelationships } = usePrefetchOCRData({
    message: messageData,
    currentPartner,
  });

  useEffect(() => {
    if (!message) {
      return;
    }

    if (message.process) {
      setDestinationProcess(message.process);
    }
    if (message.source_process) {
      setSourceProcess(message.source_process);
    }
    if (fields) {
      setProcessSpecificationFields(fields);
    }
    setDataStores(message.data_stores);
    setMessageState(message.data.state as TransactionState);
  }, [message, fields]);

  if (!messageId) {
    return;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={messageId} />;
  }

  if (isLoadingMessage || isLoadingCurrentPartner || !currentPartner || isPrefetchingOCRRelationships) {
    /*
     * We don't return MessageViewPageUI so we can start the form with the correct initial data
     * This way we avoid rendering "0 line items" in the UI when the data is loaded but not yet debounced
     */
    return <Page isLoading>{null}</Page>;
  }

  if (!messageData) {
    return <UnknownTypePlaceholder messageId={messageId} />;
  }

  if (messageData.state === TransactionState.missing) {
    return (
      <MissingMessageUI
        message={messageData}
        relatedTransactions={message.related_messages}
        currentPartner={currentPartner}
      />
    );
  }

  // Keyed by messageId so navigating between messages tears the subtree down instead of feeding new
  // props into the old form. Beyond discarding the stale result (see the document guard in
  // `datachecksResolver`), this is what resets the per-document "already reported" set in
  // `useNavigableAnnotations` — without it a genuine repeat error is swallowed (PORTAL-22X).
  return (
    <ExistingMessageUI
      key={messageId}
      message={messageData}
      messageOriginApp={message.origin_app}
      canDownloadOriginalFile={message.can_download_original_file}
      relatedTransactions={message.related_messages}
      currentPartner={currentPartner}
      processSpecFields={fields}
    />
  );
}

MessageViewPage.loader = createLoader(
  ({ params }) => useMessageWithProcessSpecification.getKey({ messageId: params.id }),
  useMessageWithProcessSpecification.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useMessageWithProcessSpecification.queryFn>>;
MessageViewPage.handle = createHandle<ResponseType, RouteParams<TransactionsRoutes.viewMessage>>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:viewDocument.breadcrumb', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
      documentType: data?.data.type
        ? t(`common:messageTypes.singular.${data.data.type}`)
        : t('common:messageTypes.singular.UNKNOWN'),
    }),
  labelAdornment: data?.data?.is_usage_based ? <AIBadge tooltipPlacement="right" /> : undefined,
  to: routeToPage(TransactionsRoutes.viewMessage, { id: params.id }),
  key: `view-message`,
}));
